import {
  createSlice,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import http from "../../utils/axios";
import { config } from "../../utils/config";

// Get business dashboard data
export const getBusinessDashboard = createAsyncThunk(
  "businessOwner/getBusinessDashboard",
  async (_, { rejectWithValue }) => {
    try {
      const res = await http.get("/dashboard/business-owner/reports/dashboard");
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch dashboard data"
      );
    }
  }
);

// Get business owner's business details
export const getBusinessDetails = createAsyncThunk(
  "businessOwner/getBusinessDetails",
  async (_, { rejectWithValue }) => {
    try {
      const res = await http.get("/dashboard/business-owner/business");
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch business details"
      );
    }
  }
);

// Update business owner's business details
export const updateBusinessDetails = createAsyncThunk(
  "businessOwner/updateBusinessDetails",
  async (data, { rejectWithValue }) => {
    try {
      const res = await http.put("/dashboard/business-owner/business", data);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update business details"
      );
    }
  }
);

// Update business owner's discount
export const updateBusinessDiscount = createAsyncThunk(
  "businessOwner/updateBusinessDiscount",
  async (data, { rejectWithValue }) => {
    try {
      const res = await http.put("/dashboard/business-owner/business/discount", data);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update discount"
      );
    }
  }
);

// Update business owner's gallery
export const updateBusinessGallery = createAsyncThunk(
  "businessOwner/updateBusinessGallery",
  async (data, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      // Add new gallery images
      if (data.gallery && data.gallery.length > 0) {
        data.gallery.forEach((file, index) => {
          if (file instanceof File) {
            formData.append(`gallery[${index}]`, file);
          } else if (file.file && !file.existing) {
            formData.append(`gallery[${index}]`, file.file);
          }
        });
      }

      // Add images to remove
      if (data.remove_images && data.remove_images.length > 0) {
        data.remove_images.forEach((imageUuid, index) => {
          formData.append(`remove_images[${index}]`, imageUuid);
        });
      }

      const res = await http.post("/dashboard/business-owner/business/gallery", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update gallery"
      );
    }
  }
);

// Set primary image
export const setPrimaryImage = createAsyncThunk(
  "businessOwner/setPrimaryImage",
  async (imageUuid, { rejectWithValue }) => {
    try {
      const res = await http.patch("/business-owner/business/gallery/set-primary", {
        image_uuid: imageUuid,
      });
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to set primary image"
      );
    }
  }
);

// Fetch services by category slug (public API)
export const fetchServicesBySlug = createAsyncThunk(
  "businessOwner/fetchServicesBySlug",
  async (slug, { rejectWithValue }) => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/public/categories/${slug}/services`);
      if (!response.ok) {
        throw new Error('Failed to fetch services');
      }
      const data = await response.json();
      return data;
    } catch (err) {
      return rejectWithValue(
        err.message || "Failed to fetch services"
      );
    }
  }
);

// Fetch payment modes for business owner
export const fetchBusinessOwnerPaymentModes = createAsyncThunk(
  "businessOwner/fetchPaymentModes",
  async (_, { rejectWithValue }) => {
    try {
      const res = await http.get("/dashboard/business-owner/payment-modes");
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch payment modes"
      );
    }
  }
);

const businessOwnerSlice = createSlice({
  name: "businessOwner",
  initialState: {
    business: null,
    dashboard: null,
    loading: false,
    dashboardLoading: false,
    error: null,
    dashboardError: null,
    updateStatus: "idle",
    updateError: null,
    services: [],
    servicesLoading: false,
    servicesError: null,
    paymentModes: [],
    paymentModesLoading: false,
    paymentModesError: null,
  },
  reducers: {
    resetUpdateStatus: (state) => {
      state.updateStatus = "idle";
      state.updateError = null;
    },
    resetServicesState: (state) => {
      state.services = [];
      state.servicesLoading = false;
      state.servicesError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get dashboard data
      .addCase(getBusinessDashboard.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(getBusinessDashboard.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.dashboard = action.payload.data;
      })
      .addCase(getBusinessDashboard.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload;
      })

      // Get business details
      .addCase(getBusinessDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBusinessDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.business = action.payload.data;
      })
      .addCase(getBusinessDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update business details
      .addCase(updateBusinessDetails.pending, (state) => {
        state.updateStatus = "loading";
        state.updateError = null;
      })
      .addCase(updateBusinessDetails.fulfilled, (state, action) => {
        state.updateStatus = "success";
        state.business = action.payload.data;
      })
      .addCase(updateBusinessDetails.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError = action.payload;
      })

      // Update discount
      .addCase(updateBusinessDiscount.pending, (state) => {
        state.updateStatus = "loading";
        state.updateError = null;
      })
      .addCase(updateBusinessDiscount.fulfilled, (state, action) => {
        state.updateStatus = "success";
        state.business = action.payload.data;
      })
      .addCase(updateBusinessDiscount.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError = action.payload;
      })

      // Update gallery
      .addCase(updateBusinessGallery.pending, (state) => {
        state.updateStatus = "loading";
        state.updateError = null;
      })
      .addCase(updateBusinessGallery.fulfilled, (state, action) => {
        state.updateStatus = "success";
        // Refresh business details after gallery update
      })
      .addCase(updateBusinessGallery.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError = action.payload;
      })

      // Set primary image
      .addCase(setPrimaryImage.pending, (state) => {
        state.updateStatus = "loading";
        state.updateError = null;
      })
      .addCase(setPrimaryImage.fulfilled, (state, action) => {
        state.updateStatus = "success";
      })
      .addCase(setPrimaryImage.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError = action.payload;
      })

      // Fetch services by slug
      .addCase(fetchServicesBySlug.pending, (state) => {
        state.servicesLoading = true;
        state.servicesError = null;
      })
      .addCase(fetchServicesBySlug.fulfilled, (state, action) => {
        state.servicesLoading = false;
        state.services = action.payload.data || [];
      })
      .addCase(fetchServicesBySlug.rejected, (state, action) => {
        state.servicesLoading = false;
        state.servicesError = action.payload;
      })

      // Fetch payment modes for business owner
      .addCase(fetchBusinessOwnerPaymentModes.pending, (state) => {
        state.paymentModesLoading = true;
        state.paymentModesError = null;
      })
      .addCase(fetchBusinessOwnerPaymentModes.fulfilled, (state, action) => {
        state.paymentModesLoading = false;
        state.paymentModes = action.payload.data || [];
      })
      .addCase(fetchBusinessOwnerPaymentModes.rejected, (state, action) => {
        state.paymentModesLoading = false;
        state.paymentModesError = action.payload;
      });
  },
});

export const { resetUpdateStatus, resetServicesState } = businessOwnerSlice.actions;
export default businessOwnerSlice.reducer;
